/**
 * Notification Service
 *
 * Firestore CRUD operations for the notifications system.
 * Notifications are organization-scoped.
 */

import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  Timestamp,
  serverTimestamp,
  writeBatch,
  arrayUnion,
  QueryConstraint,
  QueryDocumentSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../config/firebase';

// ============================================================================
// TYPES
// ============================================================================

export type NotificationType =
  | 'execution_complete'
  | 'execution_failed'
  | 'esim_provisioning_failure'
  | 'schedule_triggered'
  | 'device_offline'
  | 'device_online'
  | 'system';

export type NotificationSeverity = 'critical' | 'warning' | 'info';

export interface NotificationMetadata {
  executionId?: string;
  flowId?: string;
  flowName?: string;
  deviceId?: string;
  deviceName?: string;
  modemId?: string;
  modemName?: string;
  scheduleId?: string;
  scheduleName?: string;
  errorMessage?: string;
  [key: string]: string | undefined;
}

export interface Notification {
  id: string;
  type: NotificationType;
  /** Severity drives icon color + toast duration. Missing → 'info'. */
  severity: NotificationSeverity;
  title: string;
  message: string;
  createdAt: Date;
  /**
   * Per-user read state — `true` when the current viewer's uid is in `readBy`.
   * Computed at parse time from `readBy` (NOT the legacy org-wide `read` field),
   * so one viewer marking-read doesn't clear it for the whole org.
   */
  read: boolean;
  /** Per-user read tracking: uids that have read this notification. */
  readBy: string[];
  readAt?: Date;
  metadata: NotificationMetadata;
  /** Optional explicit click destination; overrides metadata-derived nav. */
  route?: string;
  userId?: string; // Optional: for user-specific notifications
}

export interface CreateNotificationParams {
  type: NotificationType;
  severity?: NotificationSeverity;
  title: string;
  message: string;
  metadata?: NotificationMetadata;
  route?: string;
  userId?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getNotificationsCollection = (organizationId: string) =>
  collection(db, 'organizations', organizationId, 'notifications');

const parseNotification = (doc: any, currentUserId?: string): Notification => {
  const data = doc.data();
  const readBy: string[] = Array.isArray(data.readBy) ? data.readBy : [];
  // Per-user read: am *I* in readBy? Fall back to the legacy org-wide `read`
  // only for old docs that predate readBy (no readBy AND no migration).
  const read = currentUserId
    ? readBy.includes(currentUserId)
    : data.read || false;
  return {
    id: doc.id,
    type: data.type,
    severity: (data.severity as NotificationSeverity) || 'info',
    title: data.title,
    message: data.message,
    createdAt: data.createdAt?.toDate() || new Date(),
    read,
    readBy,
    readAt: data.readAt?.toDate(),
    metadata: data.metadata || {},
    route: data.route,
    userId: data.userId,
  };
};

// ============================================================================
// SERVICE
// ============================================================================

export const notificationService = {
  /**
   * Get notifications for an organization.
   *
   * Per-user read state: Firestore can't express "uid not in readBy", so the
   * `unreadOnly` filter is applied client-side after fetching recent docs.
   */
  async getNotifications(
    organizationId: string,
    options: { limit?: number; unreadOnly?: boolean; currentUserId?: string } = {}
  ): Promise<Notification[]> {
    const { limit: limitCount = 50, unreadOnly = false, currentUserId } = options;

    const q = query(
      getNotificationsCollection(organizationId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    const notifications = snapshot.docs.map((d) => parseNotification(d, currentUserId));
    return unreadOnly ? notifications.filter((n) => !n.read) : notifications;
  },

  /**
   * Get unread notification count for the current user.
   *
   * Counts recent docs the user hasn't read (per-user `readBy`). Bounded by
   * `limit` so a huge backlog doesn't fetch everything — matches the bell's
   * "N / 99+" display, which never needs an exact count past the cap.
   */
  async getUnreadCount(
    organizationId: string,
    options: { currentUserId?: string; limit?: number } = {}
  ): Promise<number> {
    const { currentUserId, limit: limitCount = 100 } = options;
    const q = query(
      getNotificationsCollection(organizationId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((d) => parseNotification(d, currentUserId))
      .filter((n) => !n.read).length;
  },

  /**
   * Create a new notification
   */
  async createNotification(
    organizationId: string,
    params: CreateNotificationParams
  ): Promise<Notification> {
    const notificationData = {
      type: params.type,
      severity: params.severity || 'info',
      title: params.title,
      message: params.message,
      metadata: params.metadata || {},
      ...(params.route ? { route: params.route } : {}),
      userId: params.userId,
      // Dual-write: legacy org-wide `read` for one release + per-user `readBy`.
      read: false,
      readBy: [],
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(
      getNotificationsCollection(organizationId),
      notificationData
    );

    return {
      id: docRef.id,
      ...params,
      severity: params.severity || 'info',
      metadata: params.metadata || {},
      read: false,
      readBy: [],
      createdAt: new Date(),
    };
  },

  /**
   * Mark a notification as read for a single user (per-user `readBy`).
   *
   * Uses `arrayUnion(userId)` so it only adds the caller's uid — never clears
   * read state for the rest of the org. The Firestore rule restricts client
   * updates to the `readBy` field only.
   */
  async markAsRead(
    organizationId: string,
    notificationId: string,
    userId: string
  ): Promise<void> {
    const docRef = doc(getNotificationsCollection(organizationId), notificationId);
    await updateDoc(docRef, {
      readBy: arrayUnion(userId),
    });
  },

  /**
   * Mark all currently-loaded notifications as read for a single user.
   *
   * Since unread is per-user (no `read==false` query), the caller passes the
   * ids it wants marked (typically the user's loaded/unread set).
   */
  async markAllAsRead(
    organizationId: string,
    userId: string,
    notificationIds: string[]
  ): Promise<void> {
    if (!notificationIds.length) return;

    const batch = writeBatch(db);
    notificationIds.forEach((id) => {
      const ref = doc(getNotificationsCollection(organizationId), id);
      batch.update(ref, { readBy: arrayUnion(userId) });
    });

    await batch.commit();
  },

  /**
   * Delete a notification
   */
  async deleteNotification(organizationId: string, notificationId: string): Promise<void> {
    const docRef = doc(getNotificationsCollection(organizationId), notificationId);
    await deleteDoc(docRef);
  },

  /**
   * Get a page of notifications using cursor-based pagination.
   * Pass the returned `lastDoc` as `cursor` to fetch the next page.
   */
  async getNotificationsPage(
    organizationId: string,
    options: {
      limit?: number;
      cursor?: QueryDocumentSnapshot | null;
      unreadOnly?: boolean;
      currentUserId?: string;
    } = {}
  ): Promise<{ notifications: Notification[]; lastDoc: QueryDocumentSnapshot | null }> {
    const { limit: limitCount = 50, cursor = null, unreadOnly = false, currentUserId } =
      options;

    // Note: unread is per-user (no server `read==false` filter possible), so we
    // page over all docs by createdAt and filter unread client-side. The cursor
    // stays a real Firestore doc so pagination remains stable.
    const constraints: QueryConstraint[] = [
      orderBy('createdAt', 'desc'),
      limit(limitCount),
    ];
    if (cursor) constraints.push(startAfter(cursor));

    const q = query(getNotificationsCollection(organizationId), ...constraints);
    const snapshot = await getDocs(q);
    const parsed = snapshot.docs.map((d) => parseNotification(d, currentUserId));
    const notifications = unreadOnly ? parsed.filter((n) => !n.read) : parsed;
    const lastDoc = snapshot.docs[snapshot.docs.length - 1] ?? null;
    return { notifications, lastDoc };
  },

  /**
   * Delete all read notifications older than a certain date
   */
  async cleanupOldNotifications(
    organizationId: string,
    olderThan: Date
  ): Promise<number> {
    const q = query(
      getNotificationsCollection(organizationId),
      where('read', '==', true),
      where('createdAt', '<', Timestamp.fromDate(olderThan))
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) return 0;

    const batch = writeBatch(db);
    snapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    return snapshot.size;
  },

  /**
   * Subscribe to notifications in real-time
   */
  subscribeToNotifications(
    organizationId: string,
    callback: (notifications: Notification[]) => void,
    options: { limit?: number; currentUserId?: string } = {}
  ): Unsubscribe {
    const { limit: limitCount = 20, currentUserId } = options;

    const q = query(
      getNotificationsCollection(organizationId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    return onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map((d) => parseNotification(d, currentUserId));
      callback(notifications);
    });
  },
};
